import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  AssignmentInd,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ShieldOutlined,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import { saleService } from "../../services";
import { RBAC } from "../../services/api";
import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { DEFAULT_SALES_CONFIG, normalizeSalesConfig } from "../../config/permissionUtils";
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeCompany = (source) => {
  const item = source?.company || source?.branch || source;
  if (!item || typeof item !== "object") return null;

  return {
    id: item.id,
    name: item.name || item.legalName || item.alias || `Empresa ${item.id}`,
  };
};

const normalizePermission = (source) => {
  if (!source || typeof source !== "object") return null;
  return {
    id: source.id,
    code: source.code || "",
    label_es: source.label_es || "",
    name: source.name || source.code || `Permiso ${source.id}`,
    description: source.description || "",
    friendly_name:
      source.friendly_name ||
      source.label_es ||
      source.name ||
      source.code ||
      `Permiso ${source.id}`,
    caption: source.caption || source.description || "",
    example_es: source.example_es || "",
    semantic_code: source.semantic_code || source.code || "",
    canonical_code: source.canonical_code || source.semantic_code || source.code || "",
    group: source.group || "",
    is_visible: source.is_visible !== false,
    is_legacy: Boolean(source.is_legacy),
  };
};

const HIDDEN_ROLE_PERMISSION_CODES = new Set(["view_companies", "manage_companies"]);

const isVisibleRolePermission = (permission) => {
  if (permission?.is_visible === false) return false;
  const code = String(permission?.code || "").trim();
  if (HIDDEN_ROLE_PERMISSION_CODES.has(code)) return false;
  const label = String(permissionLabel(permission) || "").toLowerCase();
  return !label.includes("sucursal") && !label.includes("sucursales");
};

const normalizeRole = (source) => {
  if (!source || typeof source !== "object") return null;

  const company = normalizeCompany(source.company || source.branch || source);
  const permissions = toArray(source.permissions)
    .map(normalizePermission)
    .filter(Boolean)
    .filter(isVisibleRolePermission);

  return {
    id: source.id,
    name: source.name || source.role_name || `Rol ${source.id}`,
    is_active:
      source.is_active !== undefined
        ? Boolean(source.is_active)
        : source.active !== undefined
          ? Boolean(source.active)
          : true,
    company,
    company_id:
      source.company_id ||
      source.company?.id ||
      source.branch_id ||
      source.branch?.id ||
      company?.id ||
      "",
    permissions,
  };
};

const permissionLabel = (permission) =>
  permission?.friendly_name || permission?.label_es || permission?.name || permission?.code || "Permiso";

const permissionCodeLabel = (permission, isSuperuser = false) =>
  isSuperuser ? permission?.code || "" : "";

const permissionCaption = (permission, isSuperuser = false) => {
  const caption = String(permission?.caption || permission?.description || "").trim();
  const example = String(permission?.example_es || "").trim();
  const detail = [caption, example].filter(Boolean).join(" - ");
  const code = String(permissionCodeLabel(permission, isSuperuser) || "").trim();
  if (!isSuperuser) return detail;
  if (code && detail) return `${code} - ${detail}`;
  return code || detail || "";
};

const permissionSearchText = (permission) =>
  `${permission?.friendly_name || ""} ${permission?.label_es || ""} ${permission?.code || ""} ${permission?.name || ""} ${permission?.caption || ""} ${permission?.example_es || ""}`
    .trim()
    .toLowerCase();

const ROLE_SUGGESTIONS = [
  "Finanzas",
  "Compras",
  "Ventas",
  "Inventarios",
  "Gerencia",
  "Contabilidad",
  "Bodega",
  "Tesorería",
  "Auditoría",
  "Administrador",
  "Soporte",
  "Supervisor",
];

const PERMISSION_GROUPS = [
  {
    title: "Administracion",
    codes: ["view_companies", "manage_companies"],
  },
  {
    title: "Usuarios y Roles",
    codes: [
      "view_users",
      "manage_users",
      "view_roles",
      "manage_roles",
      "view_permissions",
      "manage_permissions",
      "assign_extra_permissions",
    ],
  },
  {
    title: "Productos",
    codes: [
      "view_products",
      "manage_products",
      "view_product_barcodes",
      "manage_product_barcodes",
      "view_categories",
      "manage_categories",
      "view_brands",
      "manage_brands",
      "view_families",
      "manage_families",
      "view_formats",
      "manage_formats",
    ],
  },
  {
    title: "Ventas/POS",
    codes: [
      "view_sales",
      "sale_create",
      "sale_discount_override",
      "sale_void",
      "sale_return",
    ],
  },
  {
    title: "Facturacion",
    codes: [
      "billing_view",
      "billing_create",
      "billing_manage",
      "billing_deferred_view",
      "billing_deferred_create",
    ],
  },
  {
    title: "Caja de Cobro",
    codes: ["cash_view", "sale_pay_now", "sale_authorize_payment", "sale_manage_transfer_accounts"],
  },
  {
    title: "Cortes/Sesiones",
    codes: [
      "cash_register_view",
      "cash_register_manage",
      "cash_session_view",
      "cash_session_manage",
      "cash_open",
      "cash_close",
      "cash_movement_view",
      "cash_movement_create",
      "cash_movement_manage",
    ],
  },
  {
    title: "Grooming",
    codes: ["grooming_view", "grooming_manage", "grooming_convert_sale", "grooming_settings_manage"],
  },
  {
    title: "Existencias",
    codes: ["inventory_stock_view"],
  },
  {
    title: "Inventario: Entradas",
    codes: ["inventory_inbound_view", "inventory_inbound_create"],
  },
  {
    title: "Inventario: Salidas",
    codes: ["inventory_outbound_view", "inventory_outbound_create"],
  },
  {
    title: "Movimientos",
    codes: ["inventory_movement_view"],
  },
  {
    title: "Conteo General",
    codes: ["inventory_count_view", "inventory_count_manage"],
  },
  {
    title: "Inventario General",
    codes: [
      "inventory_general_view",
      "inventory_general_start",
      "inventory_general_count",
      "inventory_general_review",
      "inventory_general_adjust",
      "inventory_general_cross",
      "inventory_general_apply",
      "inventory_general_cancel",
    ],
  },
  {
    title: "Conteo Selectivo",
    codes: [
      "inventory_selective_view",
      "inventory_selective_start",
      "inventory_selective_count",
      "inventory_selective_review",
      "inventory_selective_adjust",
      "inventory_selective_cross",
      "inventory_selective_apply",
      "inventory_selective_cancel",
    ],
  },
  {
    title: "Ajustes de Inventario",
    codes: ["inventory_count_settings_manage"],
  },
  {
    title: "Reportes",
    codes: ["view_reports"],
  },
];

const PERMISSION_GROUP_BY_CODE = PERMISSION_GROUPS.reduce((acc, group) => {
  group.codes.forEach((code) => {
    acc[code] = group.title;
  });
  return acc;
}, {});

const getPermissionGroupTitle = (permission) =>
  permission?.group ||
  PERMISSION_GROUP_BY_CODE[String(permission?.canonical_code || permission?.semantic_code || permission?.code || "")] ||
  "Administracion";

const getPermissionCode = (permission) =>
  String(permission?.canonical_code || permission?.semantic_code || permission?.code || "").trim();

const BILLING_PERMISSION_CODES = [
  "billing_view",
  "billing_create",
  "billing_manage",
  "billing_deferred_view",
  "billing_deferred_create",
];

const DEFERRED_BILLING_PERMISSION_CODES = [
  "billing_deferred_view",
  "billing_deferred_create",
];

const getBlockedPermissionReason = (code, salesConfig) => {
  if (code === "cash_view" && salesConfig?.allow_cashier_queue === false) {
    return 'La opcion "Permitir bandeja de cobro" no esta habilitada para esta sucursal. Puedes habilitarla en Configuracion de ventas.';
  }

  if (BILLING_PERMISSION_CODES.includes(code) && salesConfig?.allow_billing === false) {
    return 'La opcion "Permitir facturacion" no esta habilitada para esta sucursal. Puedes habilitarla en Configuracion de ventas.';
  }

  if (
    DEFERRED_BILLING_PERMISSION_CODES.includes(code) &&
    salesConfig?.allow_billing !== false &&
    salesConfig?.allow_deferred_billing === false
  ) {
    return 'La opcion "Facturacion diferida" no esta habilitada para esta sucursal. Puedes habilitarla en Configuracion de ventas.';
  }

  return "";
};

const getBlockedPermissionIds = (permissionsCatalog, salesConfig) =>
  new Set(
    permissionsCatalog
      .filter((permission) => getBlockedPermissionReason(getPermissionCode(permission), salesConfig))
      .map((permission) => permission.id)
  );

const filterBlockedPermissionIds = (permissionIds, permissionsCatalog, salesConfig) => {
  const blockedIds = getBlockedPermissionIds(permissionsCatalog, salesConfig);
  return permissionIds.filter((id) => !blockedIds.has(id));
};

const SALE_PAY_NOW_RECOMMENDED_TOOLTIP =
  "Permiso recomendado. Sin bandeja de cobro, este permiso permite cobrar ventas directamente.";

function PermissionsPicker({
  permissionsCatalog = [],
  permissionIds = [],
  setPermissionIds,
  isSuperuser = false,
  salesConfig = DEFAULT_SALES_CONFIG,
}) {
  const [query, setQuery] = useState("");

  const selectedIds = useMemo(() => new Set(permissionIds), [permissionIds]);
  const blockedPermissionIds = useMemo(
    () => getBlockedPermissionIds(permissionsCatalog, salesConfig),
    [permissionsCatalog, salesConfig]
  );

  const selectedPermissions = useMemo(
    () => permissionsCatalog.filter((permission) => selectedIds.has(permission.id)),
    [permissionsCatalog, selectedIds]
  );

  const filteredPermissions = useMemo(() => {
    const normalized = String(query || "").trim().toLowerCase();
    if (!normalized) return permissionsCatalog;
    return permissionsCatalog.filter((permission) =>
      permissionSearchText(permission).includes(normalized)
    );
  }, [permissionsCatalog, query]);

  const groupedPermissions = useMemo(() => {
    const byTitle = new Map();
    filteredPermissions.forEach((permission) => {
      const title = getPermissionGroupTitle(permission);
      if (!byTitle.has(title)) byTitle.set(title, []);
      byTitle.get(title).push(permission);
    });

    return PERMISSION_GROUPS.map((group) => ({
      title: group.title,
      permissions: byTitle.get(group.title) || [],
    }))
      .concat(
        Array.from(byTitle.entries())
          .filter(([title]) => !PERMISSION_GROUPS.some((group) => group.title === title))
          .map(([title, permissions]) => ({ title, permissions }))
      )
      .filter((group) => group.permissions.length > 0);
  }, [filteredPermissions]);

  useEffect(() => {
    setPermissionIds((prev) => {
      const next = prev.filter((id) => !blockedPermissionIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [blockedPermissionIds, setPermissionIds]);

  const togglePermission = (permission, checked) => {
    const permissionId = permission?.id;
    const code = getPermissionCode(permission);
    if (getBlockedPermissionReason(code, salesConfig)) return;
    setPermissionIds((prev) => {
      if (checked) {
        if (prev.includes(permissionId)) return prev;
        return [...prev, permissionId];
      }
      return prev.filter((id) => id !== permissionId);
    });
  };

  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        label="Buscar permiso"
        placeholder="Buscar permiso..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          maxHeight: 280,
          overflowY: "auto",
          p: 0.5,
        }}
      >
        <Stack spacing={0.25}>
          {filteredPermissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
              Sin coincidencias.
            </Typography>
          ) : (
            groupedPermissions.map((group) => (
              <Box key={group.title} sx={{ py: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 700 }}>
                  {group.title}
                </Typography>
                {group.permissions.map((permission) => {
                  const checked = selectedIds.has(permission.id);
                  const code = getPermissionCode(permission);
                  const blockedReason = getBlockedPermissionReason(code, salesConfig);
                  const payNowRecommended = code === "sale_pay_now" && salesConfig?.allow_cashier_queue === false;
                  const tooltipTitle = blockedReason
                    ? blockedReason
                    : payNowRecommended
                      ? SALE_PAY_NOW_RECOMMENDED_TOOLTIP
                      : "";
                  return (
                    <Tooltip key={permission.id} title={tooltipTitle} placement="top" arrow disableHoverListener={!tooltipTitle}>
                      <Box
                        sx={{
                          borderRadius: 1,
                          border: payNowRecommended ? "1px solid rgba(46, 125, 50, 0.35)" : "1px solid transparent",
                          opacity: blockedReason ? 0.56 : 1,
                          bgcolor: payNowRecommended
                            ? "rgba(46, 125, 50, 0.08)"
                            : checked
                              ? "action.selected"
                              : "transparent",
                          px: 0.75,
                          py: 0.5,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={Boolean(blockedReason)}
                            onChange={(event) =>
                              togglePermission(permission, event.target.checked)
                            }
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: payNowRecommended ? 800 : 600 }}>
                              {permissionLabel(permission)}
                            </Typography>
                            {permissionCaption(permission, isSuperuser) ? (
                              <Typography variant="caption" color="text.secondary">
                                {permissionCaption(permission, isSuperuser)}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ))
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          px: 1,
          py: 0.75,
          maxHeight: 96,
          overflowY: "auto",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Seleccionados: {permissionIds.length}
        </Typography>
        {selectedPermissions.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
            {selectedPermissions.map((permission) => (
              <Chip
                key={permission.id}
                size="small"
                label={
                  isSuperuser && permission?.code
                    ? `${permissionLabel(permission)} (${permission.code})`
                    : permissionLabel(permission)
                }
                variant="outlined"
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

function CreateRoleForm({
  setOpen,
  companies = [],
  permissionsCatalog = [],
  roleNameSuggestions = [],
  initialCompanyId = "",
  isSuperuser = false,
  salesConfigsByBranch = {},
  onSubmit,
}) {
  const [companyId, setCompanyId] = useState(initialCompanyId || "");
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [permissionIds, setPermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const currentSalesConfig = useMemo(
    () => salesConfigsByBranch[String(companyId)] || normalizeSalesConfig(DEFAULT_SALES_CONFIG, companyId),
    [companyId, salesConfigsByBranch]
  );

  const handleSave = async () => {
    if (!companyId) {
      setSnackMsg("Selecciona una empresa.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    if (!name.trim()) {
      setSnackMsg("El nombre del rol es obligatorio.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(
        onSubmit?.({
          company: companyId,
          name: name.trim(),
          is_active: isActive,
          permission_ids: filterBlockedPermissionIds(
            permissionIds,
            permissionsCatalog,
            currentSalesConfig
          ),
        })
      );
      setSnackMsg("Rol creado correctamente.");
      setSnackSev("success");
      setSnackOpen(true);
      setTimeout(() => setOpen(false), 300);
    } catch {
      setSnackMsg("No se pudo crear el rol.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          options={roleNameSuggestions}
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          value={name}
          onInputChange={(_, value) => setName(String(value || ""))}
          onChange={(_, value) => setName(typeof value === "string" ? value : value || "")}
          renderInput={(params) => (
            <TextField {...params} label="Nombre del rol" size="small" required />
          )}
        />

        <PermissionsPicker
          permissionsCatalog={permissionsCatalog}
          permissionIds={permissionIds}
          setPermissionIds={setPermissionIds}
          isSuperuser={isSuperuser}
          salesConfig={currentSalesConfig}
        />

        <FormControlLabel
          control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />}
          label={isActive ? "Activo" : "Inactivo"}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "Guardando..." : "Crear rol"}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </>
  );
}

function EditRoleForm({
  setOpen,
  role,
  roleNameSuggestions = [],
  permissionsCatalog = [],
  isSuperuser = false,
  salesConfigsByBranch = {},
  onSubmit,
}) {
  const [name, setName] = useState(role?.name || "");
  const [isActive, setIsActive] = useState(Boolean(role?.is_active));
  const [permissionIds, setPermissionIds] = useState(
    toArray(role?.permissions).map((permission) => permission.id)
  );
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const roleCompanyId = role?.company_id || role?.company?.id || "";
  const currentSalesConfig = useMemo(
    () =>
      salesConfigsByBranch[String(roleCompanyId)] ||
      normalizeSalesConfig(DEFAULT_SALES_CONFIG, roleCompanyId),
    [roleCompanyId, salesConfigsByBranch]
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setSnackMsg("El nombre del rol es obligatorio.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(
        onSubmit?.({
          name: name.trim(),
          is_active: isActive,
          permission_ids: filterBlockedPermissionIds(
            permissionIds,
            permissionsCatalog,
            currentSalesConfig
          ),
        })
      );
      setSnackMsg("Rol actualizado correctamente.");
      setSnackSev("success");
      setSnackOpen(true);
      setTimeout(() => setOpen(false), 300);
    } catch {
      setSnackMsg("No se pudo actualizar el rol.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Autocomplete
          freeSolo
          options={roleNameSuggestions}
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          value={name}
          onInputChange={(_, value) => setName(String(value || ""))}
          onChange={(_, value) => setName(typeof value === "string" ? value : value || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Nombre del rol"
              required
            />
          )}
        />

        <PermissionsPicker
          permissionsCatalog={permissionsCatalog}
          permissionIds={permissionIds}
          setPermissionIds={setPermissionIds}
          isSuperuser={isSuperuser}
          salesConfig={currentSalesConfig}
        />

        <FormControlLabel
          control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />}
          label={isActive ? "Activo" : "Inactivo"}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </>
  );
}

export default function RoleAdminPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { isSuperuser, permissions = [], currentBranch } = useAuth();
  const branchId = currentBranch?.id || null;

  const canViewRoles = isSuperuser || permissions.includes("view_roles");
  const canManageRoles = isSuperuser || permissions.includes("manage_roles");

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [salesConfigsByBranch, setSalesConfigsByBranch] = useState({});

  const [companyFilter, setCompanyFilter] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  
  const loadCompanies = useCallback(async () => {
    try {
      if (isSuperuser) {
        const data = await RBAC.companies.list();
        const normalized = toArray(data).map(normalizeCompany).filter(Boolean);
        setCompanies(normalized);
        return;
      }

      try {
        const data = await RBAC.companiesMine();
        const normalized = toArray(data).map(normalizeCompany).filter(Boolean);
        setCompanies(normalized);
      } catch {
        const data = await RBAC.userCompanies("me");
        const normalized = toArray(data)
          .map((item) => normalizeCompany(item?.company || item))
          .filter(Boolean);
        setCompanies(normalized);
      }
    } catch {
      setCompanies([]);
    }
  }, [isSuperuser]);

  const loadPermissionCatalog = useCallback(async () => {
    try {
      const data = await RBAC.permissions();
      const normalized = toArray(data)
        .map(normalizePermission)
        .filter(Boolean)
        .filter(isVisibleRolePermission)
        .sort((left, right) =>
          String(permissionLabel(left)).localeCompare(String(permissionLabel(right)))
        );
      setPermissionCatalog(normalized);
    } catch {
      setPermissionCatalog([]);
    }
  }, []);

  const loadSalesConfigs = useCallback(async (branchIds = []) => {
    const uniqueIds = Array.from(
      new Set(
        branchIds
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      )
    );
    if (uniqueIds.length === 0) return;

    const entries = await Promise.all(
      uniqueIds.map(async (id) => {
        try {
          const data = await saleService.getSalesConfig({ branch_id: id });
          return [String(id), normalizeSalesConfig(data, id)];
        } catch {
          return [String(id), normalizeSalesConfig(DEFAULT_SALES_CONFIG, id)];
        }
      })
    );
    setSalesConfigsByBranch((prev) => ({
      ...prev,
      ...Object.fromEntries(entries),
    }));
  }, []);

  const loadRoles = useCallback(async () => {
    if (!canViewRoles) return;

    setLoading(true);
    try {
      const params = {
        company: companyFilter || undefined,
        active: activeOnly ? "true" : "false",
        search: search.trim() || undefined,
      };
      const data = await RBAC.rolesList(params);
      const normalized = toArray(data).map(normalizeRole).filter(Boolean);
      setRoles(normalized);
    } catch {
      setRoles([]);
      setSnackMsg("No se pudieron cargar los roles.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  }, [canViewRoles, companyFilter, activeOnly, search]);

  useEffect(() => {
    loadCompanies();
    loadPermissionCatalog();
  }, [loadCompanies, loadPermissionCatalog]);

  useEffect(() => {
    const companyIds = companies.map((company) => company.id);
    loadSalesConfigs([branchId, companyFilter, ...companyIds]);
  }, [branchId, companies, companyFilter, loadSalesConfigs]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const roleNameSuggestions = useMemo(() => {
    const dynamicSuggestions = Array.from(
      new Set(roles.map((role) => String(role.name || "").trim()).filter(Boolean))
    );
    return Array.from(new Set([...ROLE_SUGGESTIONS, ...dynamicSuggestions]));
  }, [roles]);

  const handleCreateRole = async (payload) => {
    await RBAC.createRole(payload);
    setSnackMsg("Rol creado correctamente.");
    setSnackSev("success");
    setSnackOpen(true);
    await loadRoles();
  };

  const handleEditRole = async (payload) => {
    if (!selectedRole?.id) return;
    await RBAC.updateRole(selectedRole.id, payload);
    setSnackMsg("Rol actualizado correctamente.");
    setSnackSev("success");
    setSnackOpen(true);
    await loadRoles();
  };

  const handleToggleRole = useCallback(
    async (row) => {
      try {
        await RBAC.updateRole(row.id, { is_active: !row.is_active });
        setSnackMsg(row.is_active ? "Rol desactivado." : "Rol activado.");
        setSnackSev("success");
        setSnackOpen(true);
        await loadRoles();
      } catch {
        setSnackMsg("No se pudo actualizar el estado del rol.");
        setSnackSev("error");
        setSnackOpen(true);
      }
    },
    [loadRoles]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
   {
  field: "name",
  headerName: "Rol",
  minWidth: 230,
  flex: 1,
  renderCell: ({ row }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        gap: 1,
      }}
    >
      <ShieldOutlined
        fontSize="small"
        color={row.is_active ? "primary" : "disabled"}
      />

      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, lineHeight: 1.1 }}
        >
          {row.name}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.1 }}
        >
          {row.is_active ? "Activo" : "Inactivo"}
        </Typography>
      </Box>
    </Box>
  ),
},
      {
        field: "company",
        headerName: "Empresa",
        minWidth: 210,
        flex: 1,
        renderCell: ({ row }) => {
          const company = row.company || {};
          const initials = (company.name || "EM")
            .split(" ")
            .map((part) => part?.[0] || "")
            .join("")
            .slice(0, 2)
            .toUpperCase();

          return (
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ height: "100%" }}>
              <Avatar sx={{ width: 30, height: 30, fontSize: "0.75rem", bgcolor: "primary.main" }}>
                {initials}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {company.name || "Sin empresa"}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "permissions",
        headerName: "Permisos",
        minWidth: 280,
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => {
          const list = toArray(value);
          if (!list.length) return <Typography variant="caption">Sin permisos</Typography>;

          const visible = list.slice(0, 3);
          const hiddenCount = list.length - visible.length;

          return (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
              {visible.map((permission) => (
                <Tooltip
                  key={permission.id || permission.code}
                  title={permissionCaption(permission, isSuperuser) || permissionLabel(permission)}
                >
                  <Chip
                    size="small"
                    label={
                      isSuperuser && permission?.code
                        ? `${permissionLabel(permission)} (${permission.code})`
                        : permissionLabel(permission)
                    }
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                </Tooltip>
              ))}
              {hiddenCount > 0 && (
                <Chip size="small" label={`+${hiddenCount}`} color="default" />
              )}
            </Stack>
          );
        },
      },
      {
        field: "is_active",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activo" : "Inactivo"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar rol">
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageRoles}
                  onClick={() => {
                    setSelectedRole(row);
                    setEditOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canManageRoles}
                  color={row.is_active ? "error" : "success"}
                  onClick={() => handleToggleRole(row)}
                >
                  {row.is_active ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canManageRoles, handleToggleRole, isSuperuser]
  );

  if (!canViewRoles) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver el modulo de roles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
<MainText title={'Roles'} subtitle={'Administracion de roles'} icon={AssignmentInd} width="50%" />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar">
            <IconButton onClick={loadRoles}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageRoles && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Crear rol
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 230 }}>
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
          >
            <MenuItem value="">(Todas)</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Buscar rol..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Switch checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />
          }
          label={activeOnly ? "Activos" : "Inactivos"}
        />
      </Stack>

      <Box sx={{ height: isMobile ? "calc(100vh - 280px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Box>

      <CustomModal
        open={createOpen}
        setOpen={setCreateOpen}
        title="Crear rol"
        component={CreateRoleForm}
        componentProps={{
          companies,
          permissionsCatalog: permissionCatalog,
          roleNameSuggestions,
          initialCompanyId: companyFilter || "",
          isSuperuser,
          salesConfigsByBranch,
          onSubmit: handleCreateRole,
        }}
        width={isMobile ? "92%" : "620px"}
      />

      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title="Editar rol"
        component={EditRoleForm}
        componentProps={{
          role: selectedRole,
          roleNameSuggestions,
          permissionsCatalog: permissionCatalog,
          isSuperuser,
          salesConfigsByBranch,
          onSubmit: handleEditRole,
        }}
        width={isMobile ? "92%" : "620px"}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
